import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { ReceiveApiAdapter } from '../adapters/ReceiveApiAdapter';
import { apiClientWH } from '@shared/services/ApiClient';

export const useAssignCart = (returnedId: number) => {
  const { t } = useTranslation('receiveModule');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // 🎯 Adapter para asignar carrito
  const receiveAdapter = new ReceiveApiAdapter(apiClientWH);

  // 🎯 Mutation para asignar carrito
  const assignCartMutation = useMutation({
    mutationFn: (cartCode: string) =>
      receiveAdapter.assignCartToReception(returnedId, { cart_code: cartCode }),
    onSuccess: (response) => {
      // 🎯 Considerar exitosa cualquier respuesta que contenga data
      if (response.data || response.success) {
        toast({
          title: t('toasts.cart_assigned.success'),
          description: response.message || t('toasts.cart_assigned.success'),
          variant: 'default',
        });

        // 🎯 Invalidar queries relacionadas
        queryClient.invalidateQueries({
          queryKey: ['returnedListForReceive', returnedId],
        });
        queryClient.invalidateQueries({ queryKey: ['logisticStatusCounts'] });
        queryClient.invalidateQueries({ queryKey: ['returned'] });
      } else {
        toast({
          title: t('toasts.cart_assigned.error'),
          description: response.message || t('toasts.cart_assigned.error'),
          variant: 'destructive',
        });
      }
    },
    onError: (error: any) => {
      console.log('🔍 useAssignCart - Error:', error);

      let errorMessage = t('toasts.cart_assigned.unexpected');

      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast({
        title: t('toasts.cart_assigned.error'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // 🎯 Función wrapper para manejar callbacks
  const assignCart = (
    cartCode: string,
    callbacks?: { onSuccess?: () => void; onError?: () => void }
  ) => {
    assignCartMutation.mutate(cartCode, {
      onSuccess: (response) => {
        // 🎯 Considerar exitosa cualquier respuesta que contenga data
        if (response.data || response.success) {
          callbacks?.onSuccess?.();
        } else {
          callbacks?.onError?.();
        }
      },
      onError: () => {
        callbacks?.onError?.();
      },
    });
  };

  return {
    assignCart,
    isAssigning: assignCartMutation.isPending,
  };
};
